import React from 'react';
import { t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions, {
  FrequentlyAskedQuestion,
} from '@rover/components/src/FrequentlyAskedQuestions';
import { Flex, Link, Paragraph } from '@rover/kibble/core';

import { widthArray } from '../RoverProtect.utils';

export type Props = {
  roverGuaranteeUrl?: string;
  veterinaryCareLimit?: string;
};

export type StyledLinkProps = {
  href: string;
  children: string;
};

const StyledLink = ({ href, children }: StyledLinkProps): JSX.Element => (
  <Link href={href} underline={false} sx={{ verticalAlign: 'center' }}>
    {children}
  </Link>
);

const FAQSection = ({ roverGuaranteeUrl, veterinaryCareLimit }: Props): JSX.Element => {
  const QUESTIONS_AND_ANSWERS = [
    {
      question: t`Do I have to pay extra for these safety features?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            No, all of these benefits and tools are included with all pet care services booked on
            the platform.
          </Trans>
        </Paragraph>
      ),
    },
    {
      question: t`Am I protected if I book with a sitter off the platform?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            No, benefits like the{' '}
            <StyledLink href={roverGuaranteeUrl || '/rover-guarantee'}>Rover Guarantee</StyledLink>,
            24/7 support, and{' '}
            <StyledLink href="/terms/reservation-protection/">reservation protection </StyledLink>
            are only included when you book and pay through the platform. This also gives you a
            record of any messages between you and your sitter in your app inbox.
          </Trans>
        </Paragraph>
      ),
    },
    {
      question: t`What happens if my sitter cancels my booking?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            In the rare instance a sitter or dog walker needs to cancel last minute, we’ve got your
            back. Every booking made on the platform includes{' '}
            <StyledLink href="/terms/reservation-protection/">reservation protection</StyledLink>.
            This includes a full refund if your booking is cancelled within seven days of the
            service start date, a credit toward the cost of another booking, and help finding a new
            sitter if needed.
          </Trans>
        </Paragraph>
      ),
    },
    {
      question: t`Are my payments secure?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            Protecting your personal information is something we take seriously. When you book and
            pay on the platform, your bank account details are secured and your payment data is
            encrypted. Pet parents are charged before the booking begins and sitters are paid 48
            hours after it ends, keeping payments fair and timely for both parties.
          </Trans>
        </Paragraph>
      ),
    },
    {
      question: t`Do you have any additional safety tips?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            Yes, we provide a variety of safety resources for pet parents and sitters in our{' '}
            <StyledLink href="/blog/safety/">safety center</StyledLink>. This includes an overview
            of our safety features, a Meet & Greet guide with example questions to ask, and top tips
            from pet sitters with amazing safety records.
          </Trans>
        </Paragraph>
      ),
    },
    {
      question: t`What happens if something goes wrong during my booking?`,
      answer: (
        <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
          <Trans>
            In the rare instance that something goes wrong during a booking, we've got your back.
            Every booking made on the platform is backed by the{' '}
            <StyledLink href={roverGuaranteeUrl || '/rover-guarantee'}>Rover Guarantee</StyledLink>{' '}
            which includes 24/7 support and up to {veterinaryCareLimit} in vet care for eligible
            claims. We care about the health and well-being of your pets and want to give pet care
            providers the confidence to act quickly in an emergency. We will also take action in
            response to conduct that is inappropriate, unsafe, or dishonest, as explained in our{' '}
            <StyledLink href="/terms/tos/">Terms of Service</StyledLink>.
          </Trans>
        </Paragraph>
      ),
    },
  ];

  return (
    <Flex sx={{ margin: '0 auto' }} px="4x">
      <Flex>
        <FrequentlyAskedQuestions width={widthArray}>
          <FrequentlyAskedQuestions.Header>
            <Trans>FAQs</Trans>
          </FrequentlyAskedQuestions.Header>
          {QUESTIONS_AND_ANSWERS.map(({ question, answer }, idx) => (
            <FrequentlyAskedQuestion isOpen={idx === 0} key={idx} question={question}>
              {answer}
            </FrequentlyAskedQuestion>
          ))}
        </FrequentlyAskedQuestions>
      </Flex>
    </Flex>
  );
};

export default FAQSection;
